/**
 * Phase 4 — Helpers Sentry frontend pour la stratégie observabilité.
 *
 * Patterns à utiliser en remplacement des `.catch(() => {})` silencieux
 * qui masquent des erreurs API sans les remonter. Source de la
 * recommandation : audit Phase 4 — 9 patterns silencieux identifiés
 * dont 7 critiques (contact form, notifications, view tracking, etc.).
 *
 * Usage :
 *
 *     import { captureSilent } from "../utils/sentryHelpers";
 *
 *     api.post("/listings/123/view")
 *       .catch((e) => captureSilent(e, { context: "listing_view_track" }));
 *
 * `captureSilent` :
 *   - Logge en console (dev visibility immédiate)
 *   - Remonte à Sentry avec contexte si SDK chargé
 *   - Ne plante JAMAIS le caller (try/catch interne)
 *
 * Différence avec un `try/catch + console.error` brut : on standardise
 * le tag `silent_capture=true` côté Sentry, ce qui permet de filtrer
 * les incidents "non-bloquants" dans les dashboards d'alerting.
 */
import { avecSentry } from "./sentryDiffere";

/**
 * Capture une erreur de manière non-bloquante.
 *
 * @param {Error|any} error - L'erreur capturée par .catch(...)
 * @param {Object} [opts]
 * @param {string} [opts.context] - Identifiant logique du flow (ex:
 *   "listing_view_track", "contact_form_submit"). Permet de regrouper
 *   les incidents similaires dans Sentry via fingerprint.
 * @param {Object} [opts.tags] - Tags additionnels (faible cardinalité).
 * @param {Object} [opts.extra] - Contexte additionnel (haute cardinalité).
 * @param {string} [opts.level="warning"] - Niveau Sentry.
 */
export function captureSilent(error, opts = {}) {
  const { context = "unknown", tags = {}, extra = {}, level = "warning" } = opts;
  try {
    // Toujours logger console — visibilité dev même sans Sentry.
    // eslint-disable-next-line no-console
    console.warn(
      `[SILENT_CAPTURE] context=${context}`,
      error?.message || String(error || ""),
      { tags, extra },
    );
  } catch (_) { /* never break caller */ }

  avecSentry((Sentry) => {
    if (typeof Sentry.withScope === "function") {
      Sentry.withScope((scope) => {
        scope.setTag("silent_capture", "true");
        scope.setTag("capture_context", context);
        Object.entries(tags).forEach(([k, v]) => {
          try { scope.setTag(k, String(v).slice(0, 200)); } catch (_) {}
        });
        Object.entries(extra).forEach(([k, v]) => {
          try { scope.setExtra(k, v); } catch (_) {}
        });
        scope.setLevel(level);
        Sentry.captureException(error);
      });
    }
  });
}

/**
 * Sentry v8+ : ``configureScope`` est déprécié au profit de
 * ``getCurrentScope`` (récupère le scope courant) ou ``withScope``
 * (callback temporaire). On préfère ``getCurrentScope`` pour les
 * tags persistants posés dès le mount d'une page.
 */
function _avecScope(fn) {
  avecSentry((Sentry) => {
    if (typeof Sentry.getCurrentScope === "function") fn(Sentry.getCurrentScope());
  });
}

/**
 * Pose un tag métier sur le scope Sentry courant — à appeler dans les
 * pages/composants qui ont l'entité en mémoire (listing_id, tx_id...).
 *
 * Permet de retrouver "tous les incidents sur cette annonce" dans
 * Sentry plutôt que de chercher par stack trace.
 */
export function setBusinessContext(ctx = {}) {
  try {
    const allowed = [
      "listing_id", "transaction_id", "order_id",
      "boost_id", "payment_method", "delivery_method",
    ];
    _avecScope((scope) => {
      for (const key of allowed) {
        if (ctx[key]) {
          try { scope.setTag(key, String(ctx[key]).slice(0, 200)); } catch (_) {}
        }
      }
    });
  } catch (_) { /* no-op */ }
}

/**
 * Lit le header X-Request-ID sur une réponse axios et le pose comme
 * tag Sentry pour corrélation backend ↔ frontend.
 *
 * Utilisé via un interceptor axios (cf. AuthContext.js).
 */
export function tagRequestId(response) {
  try {
    const requestId =
      response?.headers?.["x-request-id"] ||
      response?.headers?.["X-Request-ID"];
    if (!requestId) return;
    _avecScope((scope) => { scope.setTag("backend_request_id", requestId); });
  } catch (_) { /* no-op */ }
}
